import { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { useInsertFideRating, type FideCadence } from '@/api/ratings';

/**
 * FideRatingInputModal — saisie manuelle d'un record FIDE (T6 P1 Stats Hub).
 *
 * 3 cadences possibles (Standard / Rapide / Blitz), date, valeur (100-3500),
 * source_label optionnel (e.g. "Tournoi FFE Île-de-France 2026-04"),
 * source_url optionnel (lien tournoi), note optionnelle.
 *
 * Design system : pas d'emoji, Phosphor icons uniquement, tabular nums sur
 * la valeur, no exclamation, tutoiement.
 */

const TYPE_LABEL: Record<FideCadence, string> = {
  standard: 'Standard',
  rapide: 'Rapide',
  blitz: 'Blitz',
};

const TYPE_ORDER: FideCadence[] = ['standard', 'rapide', 'blitz'];

export function FideRatingInputModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const [type, setType] = useState<FideCadence>('rapide');
  const [value, setValue] = useState<string>('');
  const [ratedAt, setRatedAt] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [sourceLabel, setSourceLabel] = useState<string>('');
  const [sourceUrl, setSourceUrl] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const insertM = useInsertFideRating();

  if (!open) return null;

  function resetForm(): void {
    setValue('');
    setSourceLabel('');
    setSourceUrl('');
    setNote('');
    setSubmitError(null);
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setSubmitError(null);
    const numValue = Number(value);
    if (!Number.isFinite(numValue) || numValue < 100 || numValue > 3500) {
      setSubmitError('La valeur doit être un entier entre 100 et 3500.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ratedAt)) {
      setSubmitError('La date doit être au format YYYY-MM-DD.');
      return;
    }
    insertM.mutate(
      {
        type,
        value: Math.round(numValue),
        rated_at: ratedAt,
        ...(sourceLabel.trim() !== '' ? { source_label: sourceLabel.trim() } : {}),
        ...(sourceUrl.trim() !== '' ? { source_url: sourceUrl.trim() } : {}),
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
        onError: (err: unknown) => {
          const e = err as { body?: { error?: string; detail?: string } };
          setSubmitError(
            `Sauvegarde refusée par le serveur. ${e.body?.detail ?? e.body?.error ?? ''}`.trim(),
          );
        },
      },
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Saisir un rating FIDE"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 px-4"
      onClick={onClose}
    >
      <div
        className="bg-ink-800 border border-ink-700 rounded-xl p-6 max-w-md w-full flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-heading-3 text-paper-50">Saisir un rating FIDE</h2>
            <p className="text-body-sm text-ink-300 italic mt-1">
              Tournoi officiel, transfert FFE, etc.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-300 hover:text-paper-50 transition-colors"
          >
            <X size={20} weight="regular" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="fide-type"
              className="text-caption uppercase text-ink-300 tracking-widest"
            >
              Cadence
            </label>
            <div className="flex gap-1.5" role="radiogroup" id="fide-type">
              {TYPE_ORDER.map((t) => {
                const active = t === type;
                return (
                  <button
                    type="button"
                    key={t}
                    role="radio"
                    aria-checked={active ? 'true' : 'false'}
                    onClick={() => setType(t)}
                    className={`flex-1 px-3 py-2 text-body-sm font-mono uppercase tracking-wider rounded-md border transition-colors ${
                      active
                        ? 'border-acid-500 text-acid-500 bg-ink-900'
                        : 'border-ink-700 text-ink-300 hover:text-paper-50'
                    }`}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="fide-value"
                className="text-caption uppercase text-ink-300 tracking-widest"
              >
                Valeur ELO
              </label>
              <input
                id="fide-value"
                type="number"
                step={1}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="1743"
                className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-paper-50 tabular-nums focus:border-acid-500 focus:outline-none h-10"
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="fide-date"
                className="text-caption uppercase text-ink-300 tracking-widest"
              >
                Date
              </label>
              <input
                id="fide-date"
                type="date"
                value={ratedAt}
                onChange={(e) => setRatedAt(e.target.value)}
                className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-paper-50 tabular-nums focus:border-acid-500 focus:outline-none h-10"
                required
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="fide-source-label"
              className="text-caption uppercase text-ink-300 tracking-widest"
            >
              Source (optionnel)
            </label>
            <input
              id="fide-source-label"
              type="text"
              value={sourceLabel}
              onChange={(e) => setSourceLabel(e.target.value)}
              placeholder="Tournoi FFE Île-de-France 2026-04"
              maxLength={120}
              className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-body-sm text-paper-50 focus:border-acid-500 focus:outline-none h-10"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="fide-source-url"
              className="text-caption uppercase text-ink-300 tracking-widest"
            >
              Lien tournoi (optionnel)
            </label>
            <input
              id="fide-source-url"
              type="url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://www.echecs.asso.fr/..."
              maxLength={255}
              className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-body-sm text-paper-50 focus:border-acid-500 focus:outline-none h-10"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="fide-note"
              className="text-caption uppercase text-ink-300 tracking-widest"
            >
              Note (optionnel)
            </label>
            <input
              id="fide-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Open jeunes — gain rapide finale Tour vs Pion"
              maxLength={255}
              className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 font-mono text-body-sm text-paper-50 focus:border-acid-500 focus:outline-none h-10"
            />
          </div>

          {submitError && (
            <div
              role="alert"
              className="rounded-lg border border-coral-500/30 bg-coral-500/10 px-3 py-2 text-coral-500 text-body-sm"
            >
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-ink-700 text-paper-50 text-body-sm hover:bg-ink-900 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={insertM.isPending || value === ''}
              className="px-4 py-2 rounded-lg bg-acid-500 text-ink-900 text-body-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {insertM.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
